/* eslint-disable max-lines */
import { Banner } from '@kluein/klue-ui';
import {
  ActionIcon,
  Button,
  Group,
  Loader,
  Modal,
  Popover,
  Select,
  Stack,
  Tabs,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import {
  CalendarBlank,
  NoteBlank,
  Plus,
  WarningCircle,
  X,
} from '@phosphor-icons/react';
import sortBy from 'lodash/sortBy';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { FREQUENCY_DAYS } from 'api/types/card-schedule.types';
import CardScheduleContent from 'components/common/card-schedule-modal/CardScheduleContent';
import { useAutoGeneratePrompt } from 'components/common/card-schedule-modal/useAutoGeneratePrompt';
import { permissionIds } from 'components/common/klueCard/partials/klue-card-visibility-groups/helpers';
import { useAuth } from 'contexts/auth';
import { useNotifier, NotifierType } from 'hooks/use-notifier';
import { useResponsive } from 'hooks/use-responsive';
import useVisibilityGroups from 'hooks/use-visibility-groups/useVisibilityGroups';
import { useGetAnswerModeSourcesQuery } from 'modules/current-compete/libs/ask-klue-answer-mode-chat/api/answer-sources.api';
import { useGetResearchModeSourcesQuery } from 'modules/current-compete/libs/ask-klue-research-mode-chat/api/research-sources.api';
import { useEffectivePreviewingVisibilityGroup } from 'pages/dashboard/hooks';
import {
  filterHtmlByDataAttributes,
  sanitizeText,
} from 'pages/dashboard/utils';
import { ASK_KLUE_CARD_CREATION_FILTER } from 'pages/dashboard/utils/filter-html-by-data-attributes/filter-html-by-data-attributes.constants';
import { CardPermissions } from 'pages/profile/partials/sideAction/bulkEdit';
import store from 'store';
import {
  useCreateBoardMutation,
  useGetBoardsByRivalIdQuery,
} from 'store/api/boards';
import { useCreateCardScheduleMutation } from 'store/api/card-schedules/card-schedules.api';
import { useCreateCardMutation } from 'store/api/cards';
import { useGetUserByIdQuery } from 'store/api/users/users.api';
import TEST_IDS from 'test-ids';
import TRACKING_IDS from 'tracking-ids';

import S from './AskKlueCreateCardModal.module.css';
import { buildPrioritizedRivalsOptions } from './services/build-prioritized-rivals-options';
import { formatCardCitations } from './utils/format-card-citations';

import type { AskKlueCreateCardModalProps } from './AskKlueCreateCardModal.types';
import type { ComboboxItemGroup } from '@mantine/core';
import type { UserType } from 'api/api.types';
import type { ScheduleFrequency } from 'api/types/card-schedule.types';
import type { ChangeEvent, KeyboardEvent, ReactNode } from 'react';
import type { Dispatch, RootState } from 'store/store.types';

const selectRivals = store.select(({ rivals: { all } }) => ({
  rivals: all,
}));

type SelectedRival = { id: number; name: string; profileId: number };

type LaneOption = { id: number; name: string };

type DetailsTabProps = {
  cardTitle: string;
  onCardTitleChange: (e: ChangeEvent<HTMLInputElement>) => void;
  selectedRival: SelectedRival | undefined;
  rivalsOptions: ({ value: string; label: string } | ComboboxItemGroup)[];
  onSelectRival: (value: string | null) => void;
  boardLaneId: number | undefined;
  boardLanesOptions: { value: string; label: string }[];
  onSelectLane: (value: string | null) => void;
  onLaneCreated: (lane: LaneOption) => void;
};

const DetailsTab = ({
  cardTitle,
  onCardTitleChange,
  selectedRival,
  rivalsOptions,
  onSelectRival,
  boardLaneId,
  boardLanesOptions,
  onSelectLane,
  onLaneCreated,
}: DetailsTabProps): ReactNode => {
  const { t } = useTranslation(['Dashboard', 'Common']);
  const [createBoard] = useCreateBoardMutation();
  const [isAddingLane, setIsAddingLane] = useState(false);
  const [newLaneName, setNewLaneName] = useState('');
  const [addLaneError, setAddLaneError] = useState('');
  const [isCreatingLane, setIsCreatingLane] = useState(false);

  const closeAddLanePopover = useCallback(() => {
    setIsAddingLane(false);
    setAddLaneError('');
    setNewLaneName('');
  }, []);

  // "Latest value" ref for selectedRival so async continuations of
  // handleAddLane can read the current rival without waiting for an
  // effect to flush. Assigned during render (same pattern as
  // useAutoGeneratePrompt) so a synchronously-resolving mutation still
  // sees the up-to-date value.
  const selectedRivalRef = useRef(selectedRival);
  selectedRivalRef.current = selectedRival;

  const isMountedRef = useRef(true);
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const inFlightCreateRef = useRef<{ abort: () => void } | null>(null);

  // Abort any in-flight create on unmount so DetailsTab doesn't leave a
  // dangling request when the modal closes.
  useEffect(() => {
    return () => {
      inFlightCreateRef.current?.abort();
      inFlightCreateRef.current = null;
    };
  }, []);

  // Reset the Add Lane popover and abort any in-flight create when the
  // user switches competitor — typed input/error must not carry over and
  // the previous request's lane must not land on the new rival's profile.
  useEffect(() => {
    closeAddLanePopover();
    inFlightCreateRef.current?.abort();
    inFlightCreateRef.current = null;
  }, [selectedRival?.id, closeAddLanePopover]);

  const handleAddLane = useCallback(async () => {
    // Ref-based re-entrancy guard — synchronous, so a fast double-click
    // or repeated Enter press can't start a second mutation before the
    // isCreatingLane state has a chance to commit.
    if (inFlightCreateRef.current) return;
    const trimmed = newLaneName.trim();
    if (!trimmed || !selectedRival) return;

    const submittedRivalId = selectedRival.id;
    setAddLaneError('');
    setIsCreatingLane(true);
    const createPromise = createBoard({
      name: trimmed,
      profileId: selectedRival.profileId,
    });
    inFlightCreateRef.current = createPromise;
    try {
      const newBoard = await createPromise.unwrap();

      if (!isMountedRef.current) return;
      // Rival changed mid-flight — the lane we just created lives on the
      // previous rival's profile; don't surface it in the new rival's UI.
      if (selectedRivalRef.current?.id !== submittedRivalId) return;

      onLaneCreated({ id: Number(newBoard.id), name: trimmed });
      closeAddLanePopover();
    } catch {
      if (!isMountedRef.current) return;
      if (selectedRivalRef.current?.id !== submittedRivalId) return;
      setAddLaneError(t('Dashboard:askKlue.createCardModal.addLaneError'));
    } finally {
      if (inFlightCreateRef.current === createPromise) {
        inFlightCreateRef.current = null;
      }
      if (isMountedRef.current) {
        setIsCreatingLane(false);
      }
    }
  }, [
    newLaneName,
    selectedRival,
    createBoard,
    onLaneCreated,
    closeAddLanePopover,
    t,
  ]);

  const isLaneDisabled = !selectedRival?.id;

  return (
    <>
      <Text fw={600} fz="lg">
        {t('Dashboard:askKlue.createCardModal.description')}
      </Text>

      <Stack gap="xs">
        <Text fw={500} fz="sm">
          {t('Dashboard:askKlue.createCardModal.titleLabel')}
        </Text>
        <TextInput
          value={cardTitle}
          onChange={onCardTitleChange}
          placeholder={t('Dashboard:askKlue.createCardModal.titlePlaceholder')}
          data-test-id={TEST_IDS.askKlue.createCardModal.titleInput}
        />
      </Stack>

      <Stack gap="xs">
        <Text fw={500} fz="sm">
          {t('Dashboard:askKlue.createCardModal.boardLabel')}
        </Text>
        <Select
          searchable
          placeholder={t('Dashboard:askKlue.createCardModal.boardPlaceholder')}
          value={selectedRival?.id.toString() || null}
          data={rivalsOptions}
          onChange={onSelectRival}
          data-test-id={TEST_IDS.askKlue.createCardModal.rivalSelector}
          data-tracking-id={TRACKING_IDS.askKlue.createCardModal.rivalSelector}
        />
      </Stack>

      <Stack gap="xs">
        <Text fw={500} fz="sm">
          {t('Dashboard:askKlue.createCardModal.laneLabel')}
        </Text>
        <Group gap="xs" wrap="nowrap" align="center">
          <Select
            searchable
            placeholder={t('Dashboard:askKlue.createCardModal.lanePlaceholder')}
            value={boardLaneId?.toString() || null}
            data={boardLanesOptions}
            disabled={isLaneDisabled}
            onChange={onSelectLane}
            flex={1}
            data-test-id={TEST_IDS.askKlue.createCardModal.laneSelector}
            data-tracking-id={TRACKING_IDS.askKlue.createCardModal.laneSelector}
          />
          <Popover
            opened={isAddingLane}
            onClose={() => {
              // Keep the popover open while the request is in flight so any
              // resulting error stays visible for the user to retry.
              if (isCreatingLane) return;
              closeAddLanePopover();
            }}
            position="right"
            withArrow
            trapFocus
          >
            <Popover.Target>
              <ActionIcon
                variant="subtle"
                disabled={isLaneDisabled || isCreatingLane}
                onClick={() =>
                  isAddingLane ? closeAddLanePopover() : setIsAddingLane(true)
                }
                aria-label={t('Dashboard:askKlue.createCardModal.addLane')}
                data-test-id={TEST_IDS.askKlue.createCardModal.addLaneButton}
                data-tracking-id={
                  TRACKING_IDS.askKlue.createCardModal.addLaneButton
                }
              >
                <Plus size={20} />
              </ActionIcon>
            </Popover.Target>
            <Popover.Dropdown>
              <Stack gap="xs" w={220}>
                <TextInput
                  size="sm"
                  placeholder={t(
                    'Dashboard:askKlue.createCardModal.laneNamePlaceholder',
                  )}
                  value={newLaneName}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    setNewLaneName(e.currentTarget.value);
                    setAddLaneError('');
                  }}
                  error={addLaneError || undefined}
                  disabled={isCreatingLane}
                  onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                    if (e.key === 'Enter' && !isCreatingLane) {
                      handleAddLane();
                    }
                  }}
                  data-autofocus
                  data-test-id={TEST_IDS.askKlue.createCardModal.addLaneInput}
                />
                <Group justify="flex-end" gap="xs">
                  <Button
                    size="xs"
                    variant="subtle"
                    color="gray"
                    onClick={closeAddLanePopover}
                    disabled={isCreatingLane}
                    data-test-id={
                      TEST_IDS.askKlue.createCardModal.addLaneCancel
                    }
                  >
                    {t('Common:actions.cancel')}
                  </Button>
                  <Button
                    size="xs"
                    onClick={handleAddLane}
                    loading={isCreatingLane}
                    disabled={!newLaneName.trim() || isCreatingLane}
                    data-test-id={
                      TEST_IDS.askKlue.createCardModal.addLaneSubmit
                    }
                    data-tracking-id={
                      TRACKING_IDS.askKlue.createCardModal.addLaneSubmit
                    }
                  >
                    {t('Dashboard:askKlue.createCardModal.addLaneSubmit')}
                  </Button>
                </Group>
              </Stack>
            </Popover.Dropdown>
          </Popover>
        </Group>
      </Stack>

      <Stack gap="xs">
        <Text fw={500} fz="sm">
          {t('Dashboard:askKlue.createCardModal.visibilityLabel')}
        </Text>
        <CardPermissions
          visibleToDefault={permissionIds.curatorsOnly}
          showTitle={false}
        />
      </Stack>
    </>
  );
};

const DETAILS_TAB = 'details';
const SCHEDULED_UPDATES_TAB = 'scheduled-updates';

const AskKlueCreateCardModal = ({
  answer,
  question,
  onClose,
  researchMode = false,
  disabledSourceTypes = [],
  userMessageCount = 0,
}: AskKlueCreateCardModalProps) => {
  const { t } = useTranslation(['Dashboard', 'Common']);
  const { notify } = useNotifier();
  const { maxWidth } = useResponsive();
  const { isCurator } = useAuth();
  // Covers both view-as flavors (general + AskKlue-specific) so the
  // BE returns the consumer surface for whichever signal is active.
  const previewingVisibilityGroup = useEffectivePreviewingVisibilityGroup();
  const {
    visibilityGroups: { fetchVisibilityGroupsFromApi },
    cards: { deselectAllCards },
    cardsSources: { addCardSource: addCardSourceToRedux },
  } = useDispatch<Dispatch>();

  // Board/Lane Selection
  const [selectedRival, setSelectedRival] = useState<
    SelectedRival | undefined
  >();
  const [boardOptions, setBoardOptions] = useState<LaneOption[]>([]);
  const [boardLaneId, setBoardLaneId] = useState<number | undefined>();

  // Card Title
  const [cardTitle, setCardTitle] = useState<string>(() =>
    question.length > 100 ? `${question.substring(0, 97)}...` : question,
  );

  // Schedule state
  const [scheduleFrequency, setScheduleFrequency] =
    useState<ScheduleFrequency>('unscheduled');
  const [schedulePrompt, setSchedulePrompt] = useState<string>(question);
  const [scheduleResearchMode, setScheduleResearchMode] =
    useState<boolean>(researchMode);
  const [scheduleDisabledSources, setScheduleDisabledSources] =
    useState<string[]>(disabledSourceTypes);

  const [activeTab, setActiveTab] = useState<string | null>(DETAILS_TAB);

  // Pick the source surface that matches the schedule being created so its
  // SourceFilterButton offers the right options. Drive this off
  // `scheduleResearchMode` (the schedule's own mode, which is also the
  // value persisted on save) rather than the chat-level `researchMode`
  // prop — they happen to be equal today (the Switch is disabled in
  // CardScheduleContent) but the two are semantically distinct, and a
  // future enable of the Switch would silently desync the fetched
  // sources from what the user picks. Research mode uses research-mode
  // sources; everything else uses answer-mode sources.
  //
  // Curators in answer mode never see the source dropdown (the BE
  // also gives them a cards+alerts-only surface with no UI filtering), so
  // skip the answer-mode sources fetch for that case — passing undefined
  // through to CardScheduleContent makes the filter button hide via the
  // existing `(sources?.length ?? 0) > 0` visibility gate. Exception:
  // when the curator is in view-as mode, restore the consumer dropdown
  // so the preview faithfully reflects what a consumer would see.
  const skipAnswerSourcesForCurator =
    isCurator && !scheduleResearchMode && previewingVisibilityGroup == null;

  const { data: researchSourcesData, isLoading: isLoadingResearchSources } =
    useGetResearchModeSourcesQuery(undefined, {
      skip: !scheduleResearchMode,
    });
  const { data: answerSourcesData, isLoading: isLoadingAnswerSources } =
    useGetAnswerModeSourcesQuery(
      { previewingVisibilityGroup: previewingVisibilityGroup ?? undefined },
      {
        skip: scheduleResearchMode || skipAnswerSourcesForCurator,
      },
    );

  let scheduleSourcesData: typeof researchSourcesData | undefined;
  if (scheduleResearchMode) {
    scheduleSourcesData = researchSourcesData;
  } else if (!skipAnswerSourcesForCurator) {
    scheduleSourcesData = answerSourcesData;
  }
  const isLoadingScheduleSources = scheduleResearchMode
    ? isLoadingResearchSources
    : isLoadingAnswerSources;

  // Error Handling
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  // Loading State for entire card creation operation
  const [isCreatingCardLoading, setIsCreatingCardLoading] = useState(false);

  // User for author field
  const user = useSelector<RootState, UserType | null>(
    (state) => state.auth.user,
  );

  // KlueAI user for card authorship
  const { data: klueAIUser } = useGetUserByIdQuery('klue_ai');

  // Auto-generate prompt from conversation history
  // Skip synthesis for single-turn chats — the original question is already the best prompt
  const threadId =
    researchMode && userMessageCount > 1 ? answer.thread_id : undefined;
  const {
    isGenerating: isGeneratingPrompt,
    error: promptGenerationError,
    retry: retryGeneration,
    cancel: cancelGeneration,
  } = useAutoGeneratePrompt(threadId, setSchedulePrompt);

  // Rivals from Redux
  const { rivals } = useSelector(selectRivals);

  // Board lanes for selected rival
  const { data: lanesData, refetch: refetchLanes } = useGetBoardsByRivalIdQuery(
    { path: { rivalId: selectedRival?.id ?? 0 } },
    { skip: !selectedRival?.id },
  );

  // Clear lanes from the previous rival the moment the selection changes,
  // so the Select can't briefly show stale options while the new query is
  // in flight. The data-driven effect below repopulates when the new
  // response lands.
  useEffect(() => {
    setBoardOptions([]);
  }, [selectedRival?.id]);

  useEffect(() => {
    if (lanesData) {
      // slice to remove "scratchpad" board
      const lanes = lanesData.slice(1);

      setBoardOptions(lanes.map((lane) => ({ id: lane.id, name: lane.name })));
    }
  }, [lanesData]);

  // Mutations
  const [createCard] = useCreateCardMutation();
  const [createCardSchedule] = useCreateCardScheduleMutation();

  // Visibility groups
  const {
    visibilityGroups,
    selectedGroups,
    visibleTo,
    getCardPermissionCardsData,
    setVisibleTo,
    deselectAllGroups,
  } = useVisibilityGroups();

  useEffect(() => {
    deselectAllCards();
    deselectAllGroups();
    setVisibleTo(permissionIds.curatorsOnly);
    fetchVisibilityGroupsFromApi();
  }, [
    deselectAllCards,
    deselectAllGroups,
    fetchVisibilityGroupsFromApi,
    setVisibleTo,
  ]);

  // Normalize rivals to SelectOptions with prioritization
  const rivalsOptions = useMemo(
    () =>
      buildPrioritizedRivalsOptions({
        rivals,
        prioritizedSlugs: answer.rivals || [],
        otherCompetitorsLabel: t('Dashboard:otherCompetitors'),
      }),
    [rivals, answer.rivals, t],
  );

  // Auto-select rival if exactly one is prioritized
  useEffect(() => {
    if (
      answer.rivals &&
      answer.rivals.length === 1 &&
      !selectedRival &&
      rivals.length > 0
    ) {
      const targetSlug = answer.rivals[0];
      const targetRival = rivals.find((r) => r.slug === targetSlug);

      if (targetRival) {
        setSelectedRival({
          id: targetRival.id,
          name: targetRival.name,
          profileId: targetRival.profileId,
        });
      }
    }
  }, [answer.rivals, rivals, selectedRival]);

  // Normalize board lanes to SelectOptions
  const boardLanesOptions = useMemo(
    () =>
      sortBy(
        (boardOptions || []).map((lane) => ({
          value: lane.id.toString(),
          label: lane.name,
        })),
        [(option) => option.label.toLowerCase()],
      ),
    [boardOptions],
  );

  // Form validation
  const isFormValid = useMemo(
    () => selectedRival?.id && boardLaneId && cardTitle.trim().length > 0,
    [selectedRival, boardLaneId, cardTitle],
  );

  // Handle Cancel
  const handleCancel = useCallback(() => {
    // Prevent closing while operation is in progress
    if (isCreatingCardLoading) return;

    cancelGeneration();
    setSelectedRival(undefined);
    setBoardLaneId(undefined);
    setCardTitle('');
    setScheduleFrequency('unscheduled');
    setHasError(false);
    onClose();
  }, [onClose, isCreatingCardLoading, cancelGeneration]);

  // Handle Select Rival
  const handleOnSelectRival = useCallback(
    (value: string | null) => {
      if (!value) return;

      const rival = rivals.find((r) => r.id.toString() === value);
      if (rival) {
        setSelectedRival({
          id: rival.id,
          name: rival.name,
          profileId: rival.profileId,
        });
        setBoardLaneId(undefined);
        setHasError(false);
      }
    },
    [rivals],
  );

  // Handle Select Lane
  const handleOnSelectLane = useCallback((value: string | null) => {
    if (!value) return;
    setBoardLaneId(Number(value));
    setHasError(false);
  }, []);

  // Handle lane created from the inline Add Lane popover
  const handleLaneCreated = useCallback(
    (lane: LaneOption) => {
      // Optimistically append so the Select has an option to render the new
      // boardLaneId against — the subsequent refetch reconciles.
      setBoardOptions((prev) =>
        prev.some((l) => l.id === lane.id) ? prev : [...prev, lane],
      );
      setBoardLaneId(lane.id);
      setHasError(false);
      refetchLanes();
    },
    [refetchLanes],
  );

  // Create Card with Sources (shared logic)
  const createCardWithSources = useCallback(async () => {
    if (!isFormValid || !user) return null;

    setHasError(false);
    setIsCreatingCardLoading(true);

    try {
      // Get visibility group data
      const cardPermissionData = getCardPermissionCardsData({
        visibleTo,
        selectedGroups,
        selectedCards: new Set([0]),
        visibilityGroups,
      });

      // Extract the first card data (we only passed one card)
      const cardData = cardPermissionData[0];
      const isDraft = cardData?.isDraft ?? true;

      // Filter HTML to remove collapsible sections and suggested questions
      const sanitized = sanitizeText(answer?.text || '');

      const filteredHtml = filterHtmlByDataAttributes({
        html: sanitized,
        options: ASK_KLUE_CARD_CREATION_FILTER,
      });
      const cardHtml = formatCardCitations(filteredHtml);

      // Create card
      const result = await createCard({
        data: {
          name: cardTitle,
          textHtml: cardHtml,
        },
        boardId: boardLaneId!,
        author: klueAIUser?.id ?? user.id,
        templateName: 'CardHTML',
        viewOrder: 0,
        isDraft,
        allAccess: (!isDraft && cardData?.allAccess) ?? false,
        visibilityGroups:
          isDraft || cardData?.allAccess
            ? undefined
            : Array.from(selectedGroups),
      }).unwrap();

      // Add sources to card
      let failedSourceCount = 0;
      if (answer.sources && answer.sources.length > 0) {
        const validSources = answer.sources.filter((source) => source.url);
        for (const source of validSources) {
          try {
            await addCardSourceToRedux({
              id: result.id,
              url: source.url,
              description: source.title,
            });
          } catch {
            failedSourceCount++;
          }
        }
      }

      return { result, failedSourceCount };
    } catch (error) {
      setHasError(true);
      setErrorMessage(t('Dashboard:askKlue.createCardModal.errorMessage'));
      setIsCreatingCardLoading(false);
      return null;
    }
  }, [
    isFormValid,
    user,
    klueAIUser,
    cardTitle,
    answer,
    boardLaneId,
    visibleTo,
    selectedGroups,
    visibilityGroups,
    createCard,
    addCardSourceToRedux,
    getCardPermissionCardsData,
    t,
  ]);

  // Handle Edit Card
  const handleEditCard = useCallback(async () => {
    const response = await createCardWithSources();
    if (!response) return;

    const { result, failedSourceCount } = response;
    if (result?.id) {
      if (failedSourceCount > 0) {
        notify({
          type: NotifierType.WARNING,
          message: t('Dashboard:askKlue.createCardModal.sourceAttachError', {
            count: failedSourceCount,
          }),
          icon: <WarningCircle />,
        });
      }

      // Always create card schedule to persist the answer-card link
      try {
        await createCardSchedule({
          cardId: result.id,
          payload: {
            askKlueAnswerId: answer.id,
            prompt: schedulePrompt,
            researchMode: scheduleResearchMode,
            disabledSourceTypes: scheduleDisabledSources,
            frequencyDays:
              scheduleFrequency === 'unscheduled'
                ? null
                : FREQUENCY_DAYS[scheduleFrequency],
            lastUpdatedAt: new Date().toISOString(),
          },
        }).unwrap();
      } catch {
        notify({
          type: NotifierType.ERROR,
          message: t('Dashboard:askKlue.createCardModal.scheduleError'),
          icon: <WarningCircle />,
        });
      }

      setIsCreatingCardLoading(false);
      onClose(result.id);
    }
  }, [
    createCardWithSources,
    createCardSchedule,
    onClose,
    answer.id,
    scheduleFrequency,
    schedulePrompt,
    scheduleResearchMode,
    scheduleDisabledSources,
    notify,
    t,
  ]);

  const handleCardTitleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => setCardTitle(e.target.value),
    [],
  );

  return (
    <Modal
      opened
      onClose={handleCancel}
      fullScreen={maxWidth.medium}
      centered={!maxWidth.medium}
      closeOnClickOutside={false}
      title={
        <Group gap="sm" align="center">
          <NoteBlank />
          <Title order={2} size="h4">
            {t('Dashboard:askKlue.createCardModal.title')}
          </Title>
        </Group>
      }
      closeButtonProps={{
        icon: <X />,
        disabled: isCreatingCardLoading,
      }}
      classNames={{
        close: S.closeButton,
        content: S.modalContent,
        header: S.modalHeader,
        title: S.modalTitle,
        body: S.modalBody,
      }}
      data-test-id={TEST_IDS.askKlue.createCardModal.container}
    >
      <Tabs value={activeTab} onChange={setActiveTab} className={S.tabs}>
        <Tabs.List px="lg" pt="md">
          <Tabs.Tab value={DETAILS_TAB}>
            {t('Dashboard:askKlue.createCardModal.tabs.details')}
          </Tabs.Tab>
          <Tabs.Tab
            value={SCHEDULED_UPDATES_TAB}
            c={promptGenerationError ? 'red' : undefined}
          >
            <Group gap={4} align="center" wrap="nowrap">
              {t('Dashboard:askKlue.createCardModal.tabs.scheduledUpdates')}
              {isGeneratingPrompt && activeTab !== SCHEDULED_UPDATES_TAB && (
                <Loader size="xs" color="gray" />
              )}
            </Group>
          </Tabs.Tab>
        </Tabs.List>

        <Group gap="xs" px={32} pt="lg" pb="xs">
          <CalendarBlank size={16} />
          <Text fz="sm" fw={500}>
            {t('Dashboard:askKlue.createCardModal.tabs.frequency')}:
          </Text>
          <Text
            fz="sm"
            fw={500}
            c={scheduleFrequency === 'unscheduled' ? 'dimmed' : undefined}
          >
            {t(
              `Dashboard:askKlue.createCardModal.schedule.${scheduleFrequency}`,
            )}
          </Text>
        </Group>

        {scheduleFrequency === 'unscheduled' &&
          activeTab !== SCHEDULED_UPDATES_TAB && (
            <Text
              fz="xs"
              fs="italic"
              c="dimmed"
              mt={-12}
              className={S.frequencyHint}
            >
              {t('Dashboard:askKlue.createCardModal.frequencyHint')}
            </Text>
          )}

        <Tabs.Panel value={DETAILS_TAB} className={S.tabPanel}>
          <Stack gap="lg" py="lg" px={32}>
            <DetailsTab
              cardTitle={cardTitle}
              onCardTitleChange={handleCardTitleChange}
              selectedRival={selectedRival}
              rivalsOptions={rivalsOptions}
              onSelectRival={handleOnSelectRival}
              boardLaneId={boardLaneId}
              boardLanesOptions={boardLanesOptions}
              onSelectLane={handleOnSelectLane}
              onLaneCreated={handleLaneCreated}
            />
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value={SCHEDULED_UPDATES_TAB} className={S.tabPanel}>
          <Stack gap="lg" py="lg" px={32} flex={1} mih={0}>
            <CardScheduleContent
              prompt={schedulePrompt}
              onPromptChange={setSchedulePrompt}
              frequency={scheduleFrequency}
              onFrequencyChange={setScheduleFrequency}
              researchMode={scheduleResearchMode}
              onResearchModeChange={setScheduleResearchMode}
              disabledSources={scheduleDisabledSources}
              onDisabledSourcesChange={setScheduleDisabledSources}
              sources={scheduleSourcesData?.sources}
              isLoadingSources={isLoadingScheduleSources}
              isGeneratingPrompt={isGeneratingPrompt}
              promptGenerationError={promptGenerationError}
              onRetryGeneration={retryGeneration}
              onCancelGeneration={cancelGeneration}
            />
          </Stack>
        </Tabs.Panel>
      </Tabs>

      {/* Error Banner */}
      {hasError && (
        <Banner
          message={errorMessage}
          status="error"
          variant="inline"
          size="small"
          show={hasError}
          fontWeight="normal"
        />
      )}

      {/* Button Group */}
      <Group
        justify="flex-end"
        gap="sm"
        p="lg"
        pt="md"
        className={S.buttonGroup}
      >
        <Button
          variant="subtle"
          color="gray"
          onClick={handleCancel}
          disabled={isCreatingCardLoading}
          data-test-id={TEST_IDS.askKlue.createCardModal.cancelButton}
        >
          {t('Common:actions.cancel')}
        </Button>
        <Button
          variant="filled"
          onClick={handleEditCard}
          loading={isCreatingCardLoading}
          disabled={!isFormValid || isGeneratingPrompt || promptGenerationError}
          data-test-id={TEST_IDS.askKlue.createCardModal.editCardButton}
          data-tracking-id={TRACKING_IDS.askKlue.createCardModal.editCardButton}
        >
          {t('Dashboard:askKlue.createCardModal.createAndView')}
        </Button>
      </Group>
    </Modal>
  );
};

export default AskKlueCreateCardModal;
